import React from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallerTile from './CallerTile';
import ActiveCallStack from './ActiveCallStack';
import ExpandedActiveCallGroup from './ExpandedActiveCallGroup';
import MonitoredCallTile from './MonitoredCallTile';
import {groupQueueEntries, MIN_COLLAPSED_CALLS} from './queueGrouping';

const CallerQueue = (props) => {
  const hasCallers = (props.queueEntries || []).length > 0;
  const viewportRef = React.useRef(null);
  const [expandedStacks, setExpandedStacks] = React.useState(() => new Set());
  const [now, setNow] = React.useState(() => Math.floor(Date.now() / 1000));
  const displayItems = React.useMemo(
      () => groupQueueEntries(
          props.queueEntries,
          props.callStatus?.activeCallerId,
          props.agentName,
          props.reviewerMonitoring?.listeningCallerId,
      ),
      [
        props.queueEntries,
        props.callStatus?.activeCallerId,
        props.agentName,
        props.reviewerMonitoring?.listeningCallerId,
      ],
  );

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const scrollHorizontally = (event) => {
      const hasHorizontalOverflow = viewport.scrollWidth > viewport.clientWidth;
      if (!hasHorizontalOverflow || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      viewport.scrollLeft += event.deltaY;
    };

    viewport.addEventListener("wheel", scrollHorizontally, {passive: false});
    return () => viewport.removeEventListener("wheel", scrollHorizontally);
  }, [hasCallers]);

  React.useEffect(() => {
    const hasTimedCall = (props.queueEntries || []).some(
        (entry) => entry.timeRing || (entry.answeredBy && entry.timeAnswered),
    );
    if (!hasTimedCall) return undefined;
    setNow(Math.floor(Date.now() / 1000));
    const interval = window.setInterval(
        () => setNow(Math.floor(Date.now() / 1000)),
        1000,
    );
    return () => window.clearInterval(interval);
  }, [props.queueEntries]);

  const renderCaller = (entry) => (
    <CallerTile
      key={entry.id}
      id={entry.id}
      answeredBy={entry.answeredBy}
      timeRing={entry.timeRing}
      timeAnswered={entry.timeAnswered}
      now={now}
      queueNumber={entry.queueNumber}
      selected={entry.selected}
      isHighlighted={entry.isHighlighted}
      onSelect={props.onTileSelect}
      callStatus={props.callStatus}
      time={props.time}
      micOn={props.micOn}
      setMicOn={props.setMicOn}
      setTime={props.setTime}
      callVolume={props.callVolume}
      setCallVolume={props.setCallVolume}
      reviewerMonitoring={props.reviewerMonitoring}
    />
  );

  const setStackExpanded = (stackId, expanded) => {
    setExpandedStacks((current) => {
      const next = new Set(current);
      if (expanded) next.add(stackId);
      else next.delete(stackId);
      return next;
    });
  };

  return (
    <Box sx={{height: "100%", px: {xs: 1.5, md: 2}, py: {xs: 1.5, md: 2}, minWidth: 0, minHeight: 0, overflow: "hidden"}}>
      {hasCallers ? (
        <Stack
          ref={viewportRef}
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 0.85, lg: 1 }}
          sx={{
            width: "100%",
            height: "100%",
            minWidth: 0,
            overflowX: { xs: "hidden", sm: "auto" },
            overflowY: { xs: "auto", sm: "hidden" },
            pb: 1,
            alignItems: {xs: "stretch", sm: "flex-start"},
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            scrollSnapType: {xs: "y proximity", sm: "x proximity"},
            scrollbarGutter: "stable",
          }}
        >
          {displayItems.map((item) => {
            if (item.type === "caller") return renderCaller(item.entry);
            if (item.type === "monitored-call") {
              return <MonitoredCallTile
                key={item.id}
                entry={item.entry}
                now={now}
                reviewerMonitoring={props.reviewerMonitoring}
              />;
            }

            const expanded = expandedStacks.has(item.id);
            if (!expanded && item.entries.length < MIN_COLLAPSED_CALLS) {
              return (
                <React.Fragment key={item.id}>
                  {item.entries.map(renderCaller)}
                </React.Fragment>
              );
            }

            if (expanded) {
              return (
                <ExpandedActiveCallGroup
                  key={item.id}
                  entries={item.entries}
                  now={now}
                  onCollapse={() => setStackExpanded(item.id, false)}
                  reviewerMonitoring={props.reviewerMonitoring}
                />
              );
            }

            return (
              <ActiveCallStack
                key={item.id}
                entries={item.entries}
                now={now}
                onExpand={() => setStackExpanded(item.id, true)}
                reviewerMonitoring={props.reviewerMonitoring}
              />
            );
          })}
        </Stack>
      ) : (
        <Paper
          elevation={0}
          sx={{
            height: "100%",
            minHeight: 260,
            borderRadius: "10px",
            border: "1px dashed",
            borderColor: "rgba(0, 77, 64, 0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.2,
            backgroundColor: "rgba(255,255,255,0.55)",
          }}
        >
          <CallEndIcon sx={{ fontSize: 28, color: "text.disabled" }} />
          <Typography variant="subtitle2" color="text.secondary">
            No callers in queue
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Incoming calls will appear here automatically.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

export default CallerQueue
